import { useQuery } from '@tanstack/react-query';
import { ApiError, api } from './api';
import { useAuth } from './auth';
import type { MessageRole } from './sessions';

export type SearchHit = {
  message_id: string;
  session_id: string;
  session_title: string;
  role: MessageRole;
  /** HTML snippet with <mark>…</mark> around matched terms. */
  snippet: string;
  created_at: number;
};

export const searchKeys = {
  user: (userId: number) => ['search', userId] as const,
  query: (query: string, userId: number) => ['search', userId, query] as const,
};

export function useSearch(query: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: user ? searchKeys.query(query, user.id) : ['search', 'nil'],
    enabled: query.trim().length > 0 && user != null,
    queryFn: async () => {
      const r = await api<{ query: string; hits: SearchHit[] }>(
        `/search?q=${encodeURIComponent(query)}`,
      );
      return r.hits;
    },
    // Search is cheap; keep results warm across focus/route changes.
    staleTime: 60_000,
    retry: (attempt, error) => !(error instanceof ApiError && error.status === 400) && attempt < 2,
  });
}
